/**
 * The tenant's outgoing mail setting, and the delivery log every send writes.
 *
 * Foundations 2 (Users rail), B2d-T3. decisions.md 2026-09-08 (Users rail,
 * product choices) #2: *"Email is a tenant SMTP setting, configured in Admin,
 * as ThingsBoard does. No mail server is built; invitations and notifications
 * are delivered through it."*
 *
 * ZOD IS THE TYPE SOURCE (CLAUDE.md). Everything about the noun is declared
 * here once: the transport vocabulary, the outcome vocabulary, the category
 * vocabulary, the shape the Admin screen sends and the shape it gets back.
 * `api/src/services/email.service.ts` imports these types rather than
 * re-declaring its own unions, the c34 CHECK constraints carry the same values,
 * and `packages/api/src/db/schema/email-deliveries.ts` says so in its header.
 *
 * ─── THE SECRET IS NEVER IN A SHAPE THAT LEAVES THE SERVER ────────────────
 *
 * `TenantSmtpSettingWriteSchema` has a `password` — it is the ONLY shape that
 * does, it is inbound only, and what the store holds is the AES-256-GCM
 * ciphertext `middleware/field-encryption.ts` produces. `TenantSmtpSettingSchema`
 * — the read shape, the one every response uses — has no password field at all
 * and carries `hasCredentials: boolean` instead, so "is a password configured"
 * is answerable without the password being answerable. A field that does not
 * exist cannot be leaked by a serializer that forgot to strip it.
 *
 * ─── SMTP IS NOT AN "EXTERNAL CONNECTION" ─────────────────────────────────
 *
 * decisions.md 2026-09-13 #3, the founder verbatim: *"Even on Prem people will
 * like to connect a shared outgoing mailbox by smtp or something."* So
 * `allow_external_connections` being off does not disable invitation or
 * notification mail; the tenant's own relay is mail configuration, not reaching
 * arbitrary outside systems. `policy/external-egress.ts` records the exclusion
 * and `services/__tests__/tenant-smtp-transport.integration.test.ts` asserts it
 * with the setting actually off.
 */

import { z } from 'zod';

// ─── Vocabularies ──────────────────────────────────────────────────────────

/**
 * Which relay carried the message. `none` is not a relay: it is the record
 * that no transport was configured, which is a delivery-log outcome in its own
 * right (see `EmailDeliveryOutcomeSchema`).
 */
export const EmailTransportSchema = z.enum(['tenant', 'platform', 'none']);
export type EmailTransport = z.infer<typeof EmailTransportSchema>;

/**
 * What happened. `disabled` is a RECORDED outcome, not a silence: before this
 * task the only trace of a send that never happened was a Pino line, and the
 * acceptance says in as many words that a send path whose only record is a Pino
 * line must not exist afterward.
 */
export const EmailDeliveryOutcomeSchema = z.enum(['sent', 'failed', 'disabled']);
export type EmailDeliveryOutcome = z.infer<typeof EmailDeliveryOutcomeSchema>;

/** What the message was for. The `test` category is the Admin screen's button. */
export const EmailCategorySchema = z.enum([
  'invitation',
  'password_reset',
  'mfa',
  'notification',
  'system',
  'test',
]);
export type EmailCategory = z.infer<typeof EmailCategorySchema>;

/**
 * How TLS is used on the wire.
 *
 * ONE VOCABULARY PER FIELD (the B2c-T2 lesson). The pre-existing tenant SMTP
 * config held `secure: boolean`, which cannot tell implicit TLS (SMTPS, port
 * 465) from opportunistic STARTTLS, while the platform setting next to it has
 * always used these three values. c34 normalises the stored booleans onto this
 * alphabet — `true` → `tls`, `false` → `starttls` — so the two halves of one
 * noun stop answering the same question differently.
 */
export const EmailSecureSchema = z.enum(['tls', 'starttls', 'none']);
export type EmailSecure = z.infer<typeof EmailSecureSchema>;

/**
 * The sanitized failure code a caller may branch on. Free-form SMTP server
 * responses never reach a user-facing surface; they are Pino-logged and stored
 * in the delivery log's `error` column, which only a tenant admin can read.
 */
export const EmailErrorCodeSchema = z.enum([
  'MISSING_TENANT',
  'PLATFORM_DISABLED',
  'TENANT_DISABLED',
  'CREDENTIAL_DECRYPT_FAILED',
  'TRANSPORT_BUILD_FAILED',
  'SEND_TIMEOUT',
  'SEND_FAILED',
  'NO_FROM_ADDRESS',
]);
export type EmailErrorCode = z.infer<typeof EmailErrorCodeSchema>;

// ─── The tenant setting ────────────────────────────────────────────────────

/**
 * What the store holds for a tenant's relay, minus the secret.
 *
 * `enabled` is not a feature flag (principle 8): it is the tenant's own record
 * of whether this relay should be used, exactly as `platform.smtp.enabled` is
 * for the platform default. Turning it off falls through to the platform
 * transport, which is the precedence this task does not change.
 */
export const TenantSmtpSettingSchema = z.object({
  host: z.string().min(1).max(255),
  port: z.number().int().min(1).max(65535),
  secure: EmailSecureSchema,
  /** SMTP AUTH user. Optional: an internal relay may accept unauthenticated submission. */
  username: z.string().max(320).nullable(),
  /** Whether a password is stored. THE PASSWORD ITSELF IS NEVER SERVED. */
  hasCredentials: z.boolean(),
  fromAddress: z.string().email().max(320),
  fromName: z.string().max(200).nullable(),
  replyTo: z.string().email().max(320).nullable(),
  enabled: z.boolean(),
  updatedAt: z.string(),
});
export type TenantSmtpSetting = z.infer<typeof TenantSmtpSettingSchema>;

/**
 * What the Admin screen sends. The only shape carrying a password.
 *
 * `password: null` CLEARS the stored credential; omitting the key leaves it
 * untouched. Those are different acts and a single optional string cannot
 * express both, which is why the field is `.optional()` over a nullable string
 * rather than merely optional.
 */
const tenantSmtpSettingWriteBase = z.object({
  host: z.string().min(1).max(255),
  port: z.number().int().min(1).max(65535),
  secure: EmailSecureSchema,
  username: z.string().max(320).nullable().optional(),
  password: z.string().min(1).max(1024).nullable().optional(),
  fromAddress: z.string().email().max(320),
  fromName: z.string().max(200).nullable().optional(),
  replyTo: z.string().email().max(320).nullable().optional(),
  enabled: z.boolean().default(true),
});

/**
 * The write shape, for a route that needs to add a field of its own (the PUT
 * adds `reason`). Exported as the raw shape because the schema below is a
 * refinement, and a refined schema cannot be `.extend`ed.
 */
export const tenantSmtpSettingWriteShape = tenantSmtpSettingWriteBase.shape;

/**
 * A PASSWORD WITH NOBODY TO SEND IT AS IS REFUSED (B2d-T3 review, R4).
 *
 * SMTP AUTH is a pair. The transport builder emits an `auth` block only when it
 * has both halves (`email.service.ts` `buildSmtpOptions`), so a row holding a
 * password and no username used to be accepted, stored, reported back as
 * `hasCredentials: true` and labelled "Password (stored)" on the screen — while
 * every message went out with no authentication at all and the relay answered
 * 535. The store said one thing and the wire did another, and neither said so.
 *
 * Nothing fails open (principles.md #4): the write is refused at the edge, and
 * `resolveTenantTransport` refuses to send with a row that predates this rule
 * rather than silently dropping the credential.
 */
export function refineTenantSmtpCredentialPair(
  value: { username?: string | null; password?: string | null },
  ctx: z.RefinementCtx,
): void {
  const hasPassword = typeof value.password === 'string';
  const hasUsername =
    typeof value.username === 'string' && value.username.trim().length > 0;
  if (hasPassword && !hasUsername) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['username'],
      message:
        'A username is required when a password is set — an SMTP relay cannot authenticate with a password alone.',
    });
  }
}

export const TenantSmtpSettingWriteSchema = tenantSmtpSettingWriteBase.superRefine(
  refineTenantSmtpCredentialPair,
);
export type TenantSmtpSettingWrite = z.infer<typeof TenantSmtpSettingWriteSchema>;

// ─── The delivery log ──────────────────────────────────────────────────────

/**
 * One row per send ATTEMPT — including the attempts that never opened a socket.
 * `api/src/services/email-delivery-log.service.ts` is the one writer.
 */
export const EmailDeliveryRowSchema = z.object({
  id: z.string().uuid(),
  category: EmailCategorySchema,
  toAddress: z.string(),
  subject: z.string(),
  transport: EmailTransportSchema,
  outcome: EmailDeliveryOutcomeSchema,
  errorCode: z.string().nullable(),
  error: z.string().nullable(),
  messageId: z.string().nullable(),
  createdAt: z.string(),
});
export type EmailDeliveryRow = z.infer<typeof EmailDeliveryRowSchema>;

export const EmailDeliveryListSchema = z.object({
  items: z.array(EmailDeliveryRowSchema),
  total: z.number(),
});
export type EmailDeliveryList = z.infer<typeof EmailDeliveryListSchema>;
